import { CartToken } from "@/libs/cart-token/CartToken";

// 「이 브라우저가 이 cart token 으로 GE 결제를 시작했다」는 표식. docs/global-e-storefront.md §4.7.3.
//
// 왜 필요한가: 결제가 GE 체크아웃 페이지를 떠난 채 끝나면 프론트의 비우기 신호(CONFIRMATION)가 없어서
// 카트·token 이 남는다(2026-09-29 dev 실측 — GE 복귀 주소 설정 문제로 PayPal 이 **사이트 루트**
// `/?cb=true&ts=Authorized…` 로 돌아왔다. 10-01 GE 수정 뒤엔 체크아웃 페이지로 온다). 이 표식이 있는 브라우저만 서버에 「소비됐나」를 묻고
// (`consumed-cart.ts`), 로그인 병합이 결제 중인 token 문서를 지우지 않게 한다(`shouldClaimInsteadOfMerge`).
//
// 수명:
// - 생김: `/international-checkout` 마운트(**`globalEEnabled` ON 일 때만** — 호출부가 거른다) — 그 순간의 cart token
//   으로. 재진입하면 새로 쓴다. 두 진입점(카트 버튼·`runCheckout`)은 이동 직전에 결제 종류(`intent`)를 먼저 남긴다.
// - 죽음: 소비 확인 후 비울 때 · CONFIRMATION 으로 비울 때 · 쿠키 token 이 바뀌었을 때 · 24h 경과.
// - 못 읽으면(파싱 실패·스토리지 차단): **없는 것으로 본다** = 종전 동작(묻지 않음·병합함).

export const GE_CHECKOUT_PATH = "/international-checkout";
export const GE_CHECKOUT_MARKER_KEY = "bh-ge-checkout";

/** 표식 자체의 수명. 이 뒤엔 SendOrder 가 올 수 없다고 본다. */
export const GE_CHECKOUT_MARKER_TTL_MS = 24 * 60 * 60 * 1000;
/**
 * 「결제 진행 중」으로 보는 창 — 이 동안은 로그인 병합 대신 claim 만 한다(회원 귀속 보존).
 * GE 체크아웃(주소·결제 입력·PayPal 왕복)과 SendOrder 도착까지를 덮는 값. 너무 길면 공용 브라우저에서
 * 이전 사용자의 claim 이 오래 남는다(아래 `shouldClaimInsteadOfMerge` 주석).
 * 체크아웃 재마운트가 바로구매 표식(`direct`)을 잇는 창이기도 하다(`markGeCheckoutStarted`).
 */
export const GE_CHECKOUT_IN_FLIGHT_MS = 30 * 60 * 1000;

export interface GeCheckoutMarker {
  token: string;
  startedAt: number;
  /** 체크아웃을 시작한 뒤 GE 페이지가 아닌 곳에 처음 도착한 시각 — 복귀 직후 폴링을 한 번만 하려고. */
  returnedAt?: number;
  /**
   * 이 결제가 **카트를 거치지 않은 바로구매**였나. 바로구매도 카트와 **같은 token** 에 쓰므로(`writeGuestCartForCheckout`),
   * 그 token 이 소비됐다고 카트까지 비우면 **사지 않은 카트**가 사라진다 → 바로구매면 token 만 교체하고 카트는 둔다
   * (`clearConsumedCart`). 없으면 카트 결제(카트 전체가 주문됨)로 본다.
   */
  direct?: boolean;
  /**
   * 진입점이 이동 직전에 쓴 기록이고 **아직 체크아웃 페이지가 마운트되지 않았다**(마운트가 지운다). 결제 종류(`direct`)를
   * 정하는 것은 이 기록의 **쓰기**다 — 진입점이 매번 명시하므로, 바로구매로 들어갔다 나와 카트 결제로 다시 들어오면 카트 버튼의
   * `direct:false` 가 앞선 바로구매를 덮는다(strictreview R1). 그 뒤의 재마운트는 종류를 새로 정하지 않고 잇기만 한다.
   * 이 플래그 자체는 상속 판정에 쓰이지 않는다(「진입점이 썼고 아직 마운트 전」을 알아보는 진단·테스트용).
   */
  intent?: boolean;
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function write(marker: GeCheckoutMarker): void {
  try {
    storage()?.setItem(GE_CHECKOUT_MARKER_KEY, JSON.stringify(marker));
  } catch (e) {
    // 스토리지 차단 — 표식 없음 = 종전 동작(회수 안 함 · 복귀 착지에서 병합). **조용히 넘기지 않는다** —
    // 그 브라우저에선 이 수정 전체가 꺼지므로 콘솔에서 보여야 진단된다(docs/pitfalls.md 「fail-closed 경로는 조용히 반환하지 말 것」).
    console.warn("[GlobalE] checkout marker not persisted", e);
  }
}

function readRaw(): Partial<GeCheckoutMarker> | null {
  try {
    const raw = storage()?.getItem(GE_CHECKOUT_MARKER_KEY);
    return raw ? (JSON.parse(raw) as Partial<GeCheckoutMarker>) : null;
  } catch {
    return null;
  }
}

export function clearGeCheckoutMarker(): void {
  try {
    storage()?.removeItem(GE_CHECKOUT_MARKER_KEY);
  } catch {
    /* noop */
  }
}

/**
 * 진입점이 GE 체크아웃으로 이동하기 **직전**에 결제 종류를 남긴다 — 체크아웃 페이지 마운트는 종류를 모른다.
 * **두 진입점 모두** 부른다(카트 버튼 = `direct:false`, `runCheckout` = 훅이 준 값). 명시적으로 남겨야 앞선 바로구매의
 * `direct` 가 다음 카트 결제에 새지 않는다. token 이 없으면 쓰지 않는다.
 */
export function markGeCheckoutIntent(opts: {
  direct: boolean;
  now?: number;
}): void {
  const token = CartToken.get();
  if (!token) return;
  write({
    token,
    startedAt: opts.now ?? Date.now(),
    intent: true,
    ...(opts.direct ? { direct: true } : {}),
  });
}

/**
 * `/international-checkout` 마운트 때 부른다 — 모든 진입(진입점 이동·새로고침·뒤로가기·bfcache 복원 뒤 재로드·결제
 * 복귀·직접 URL)의 공통 지점. 같은 token 의 직전 표식이 바로구매(`direct`)이고 결제 진행 창(`GE_CHECKOUT_IN_FLIGHT_MS`)
 * 안이면 **그대로 잇는다**. 결제 종류를 정하는 것은 진입점의 의도 기록(`markGeCheckoutIntent`)이고 마운트는 잇기만 한다.
 * token 이 없으면(빈 카트 진입) 쓰지 않는다.
 *
 * 왜 의도 기록에서만 잇지 않나 — PayPal 은 결제 뒤 이 페이지로 **다시 들어오고**(2026-10-01 GE 복귀 주소 수정 뒤) 그
 * 결제의 CONFIRMATION 이 그 재마운트 뒤에 온다. 그 사이 PayPal 에서 뒤로가기·새로고침으로 재마운트가 더 낄 수도 있다.
 * 재마운트가 `direct` 를 버리면 바로구매가 사지 않은 카트까지 비운다. 앞선 바로구매가 다음 카트 결제에 새는 경우는
 * 진입점이 매번 종류를 명시해 막는다(카트 버튼 = `direct:false`) — `consumed-cart-wiring.test.ts` 가 모든 진입 경로를 잠근다.
 * 남는 경우(바로구매 뒤 창 안에 카트를 고치고 **진입점 없이** 직접 URL 로 들어와 결제)는 산 카트가 남는 쪽으로 틀린다 —
 * 사지 않은 카트를 지우는 쪽보다 낫다.
 */
export function markGeCheckoutStarted(opts: { now?: number } = {}): void {
  const now = opts.now ?? Date.now();
  const token = CartToken.get();
  if (!token) return;
  const prev = readRaw();
  const age =
    typeof prev?.startedAt === "number" ? now - prev.startedAt : Infinity;
  const direct =
    prev?.token === token &&
    prev.direct === true &&
    age <= GE_CHECKOUT_IN_FLIGHT_MS;
  write({ token, startedAt: now, ...(direct ? { direct: true } : {}) });
}

/**
 * 살아 있는 표식만 돌려준다. 만료·형식 이상·**쿠키 token 과 불일치**면 지우고 null.
 * 불일치 = 이미 token 이 교체됐다(비웠다) — 그 옛 token 에 대해 더 할 일이 없다.
 */
export function readActiveGeCheckout(
  now: number = Date.now(),
): GeCheckoutMarker | null {
  let parsed: unknown;
  try {
    const raw = storage()?.getItem(GE_CHECKOUT_MARKER_KEY);
    if (!raw) return null;
    parsed = JSON.parse(raw);
  } catch {
    clearGeCheckoutMarker();
    return null;
  }
  const m = parsed as Partial<GeCheckoutMarker> | null;
  const valid =
    !!m &&
    typeof m.token === "string" &&
    typeof m.startedAt === "number" &&
    Number.isFinite(m.startedAt);
  if (
    !valid ||
    now - m.startedAt! > GE_CHECKOUT_MARKER_TTL_MS ||
    m.token !== CartToken.get()
  ) {
    clearGeCheckoutMarker();
    return null;
  }
  return m as GeCheckoutMarker;
}

/**
 * 이 페이지 로드가 체크아웃 시작 뒤 **첫 비-GE 페이지**인가 — 맞으면 표시(returnedAt)하고 true.
 * 리다이렉트 결제의 복귀 착지가 여기에 해당한다. 한 표식당 한 번만 true.
 */
export function takeFirstReturnAfterGeCheckout(
  now: number = Date.now(),
): boolean {
  const m = readActiveGeCheckout(now);
  if (!m || m.returnedAt !== undefined) return false;
  write({ ...m, returnedAt: now });
  return true;
}

/**
 * 로그인 전이 때 게스트 카트를 **병합하지 말고 claim 만** 해야 하는가.
 *
 * 병합(`mergeGuestCart`)은 token 문서를 **삭제**한다 → claim 해 둔 `ownerUid`·고정 쿠폰·GA 식별자가 사라진 채
 * 곧바로 sync 가 문서를 다시 만든다 → 그 뒤 도착한 SendOrder 가 소유자를 못 찾아 **회원 주문이 게스트로**
 * 저장된다(2026-09-29 dev 실측: PayPal 복귀 착지 `/` 에서 병합 → 51초 뒤 SendOrder → uid 없음).
 * GE 체크아웃 페이지 자체와, 그 페이지를 떠난 뒤 결제 진행 창([GE_CHECKOUT_IN_FLIGHT_MS]) 안에서는 claim 만 한다.
 *
 * ⚠️ 창을 길게 잡지 말 것 — 공용 브라우저에서 A 가 claim 하고 로그아웃한 뒤 B 가 같은 token 으로 게스트 결제하면
 * B 의 주문이 A 에게 귀속된다. 병합이 그 claim 을 지워 주던 경로를 이 창 동안 멈추는 것이므로 노출이 창 길이만큼 는다.
 *
 * 수용한 부작용: 표식은 **결제를 끝내지 않고 떠난 체크아웃**에도 남는다(결제 여부를 브라우저가 알 방법이 없다). 그래서
 * 체크아웃을 열었다 나간 뒤 30분 안에 로그인하면 병합(게스트 카트 → uid 카트 채택)과 로그인 시 sync 가 그 한 번 건너뛰어진다.
 * 로컬 카트는 그대로고, 이후 카트 변경은 store subscribe 가 sync 한다 — 잃는 것은 로그인 순간의 uid 카트 갱신뿐이다.
 */
export function shouldClaimInsteadOfMerge(
  pathname: string,
  now: number = Date.now(),
): boolean {
  if (pathname === GE_CHECKOUT_PATH) return true;
  const m = readActiveGeCheckout(now);
  return !!m && now - m.startedAt <= GE_CHECKOUT_IN_FLIGHT_MS;
}
